import React from 'react';
import { DocsLayout } from '../../components/docs/DocsLayout';
import { CodeBlock } from '../../components/docs/CodeBlock';
import { Callout } from '../../components/docs/Callout';
import { Shield, Key, AlertTriangle } from 'lucide-react';
export function AuthenticationGuidePage() {
  const authRequestCode = {
    curl: `curl https://api.obseq.com/v1/wallets \\
  -H "Authorization: Bearer sk_live_abc123..." \\
  -H "Content-Type: application/json"`,
    node: `const apiKey = process.env.OBSEQ_API_KEY;

const response = await fetch('https://api.obseq.com/v1/wallets', {
  headers: {
    'Authorization': \`Bearer \${apiKey}\`,
    'Content-Type': 'application/json'
  }
});

const data = await response.json();`,
    python: `import os
import requests

api_key = os.environ['OBSEQ_API_KEY']

response = requests.get(
    'https://api.obseq.com/v1/wallets',
    headers={
        'Authorization': f'Bearer {api_key}',
        'Content-Type': 'application/json'
    }
)

data = response.json()`,
    go: `package main

import (
    "net/http"
    "os"
)

func main() {
    apiKey := os.Getenv("OBSEQ_API_KEY")
    
    req, _ := http.NewRequest("GET", "https://api.obseq.com/v1/wallets", nil)
    req.Header.Set("Authorization", "Bearer "+apiKey)
    req.Header.Set("Content-Type", "application/json")
    
    client := &http.Client{}
    resp, _ := client.Do(req)
    defer resp.Body.Close()
}`,
    java: `import java.net.http.*;
import java.net.URI;

String apiKey = System.getenv("OBSEQ_API_KEY");

HttpRequest request = HttpRequest.newBuilder()
    .uri(URI.create("https://api.obseq.com/v1/wallets"))
    .header("Authorization", "Bearer " + apiKey)
    .header("Content-Type", "application/json")
    .GET()
    .build();

HttpClient client = HttpClient.newHttpClient();
HttpResponse<String> response = client.send(request, 
    HttpResponse.BodyHandlers.ofString());`,
    php: `<?php
$apiKey = getenv('OBSEQ_API_KEY');

$client = new GuzzleHttp\\Client();
$response = $client->get('https://api.obseq.com/v1/wallets', [
    'headers' => [
        'Authorization' => 'Bearer ' . $apiKey,
        'Content-Type' => 'application/json'
    ]
]);

$data = json_decode($response->getBody(), true);`,
    ruby: `require 'httparty'

api_key = ENV['OBSEQ_API_KEY']

response = HTTParty.get(
  'https://api.obseq.com/v1/wallets',
  headers: {
    'Authorization' => "Bearer #{api_key}",
    'Content-Type' => 'application/json'
  }
)

data = response.parsed_response`,
    dotnet: `using System;
using System.Net.Http;
using System.Net.Http.Headers;

var apiKey = Environment.GetEnvironmentVariable("OBSEQ_API_KEY");

var client = new HttpClient();
client.DefaultRequestHeaders.Authorization = 
    new AuthenticationHeaderValue("Bearer", apiKey);
client.DefaultRequestHeaders.Accept.Add(
    new MediaTypeWithQualityHeaderValue("application/json"));

var response = await client.GetAsync("https://api.obseq.com/v1/wallets");
var data = await response.Content.ReadAsStringAsync();`
  };
  return <DocsLayout breadcrumbs={[{
    label: 'Docs',
    href: '/docs'
  }, {
    label: 'Guides',
    href: '/docs/guides'
  }, {
    label: 'Authentication',
    href: '/docs/guides/authentication'
  }]} onThisPage={[{
    title: 'Overview',
    id: 'overview'
  }, {
    title: 'API Keys',
    id: 'api-keys'
  }, {
    title: 'Authentication Header',
    id: 'header'
  }, {
    title: 'Environments',
    id: 'environments'
  }, {
    title: 'Code Examples',
    id: 'examples'
  }, {
    title: 'Security Best Practices',
    id: 'security'
  }]} data-id="element-1106">
      <h1 data-id="element-1107">Authentication</h1>
      <p className="lead" data-id="element-1108">
        Obseq uses API keys to authenticate requests. All API requests must
        include your API key in the Authorization header.
      </p>

      <h2 id="overview" data-id="element-1109">Overview</h2>
      <p data-id="element-1110">
        Authentication is required for all API endpoints. Requests without valid
        authentication return a 401 Unauthorized error.
      </p>

      <h2 id="api-keys" data-id="element-1111">API Keys</h2>
      <p data-id="element-1112">
        API keys are unique identifiers that authenticate your requests. Each
        key has a prefix indicating its environment:
      </p>
      <ul data-id="element-1113">
        <li data-id="element-1114">
          <code data-id="element-1115">sk_test_...</code> - Sandbox environment (for testing)
        </li>
        <li data-id="element-1116">
          <code data-id="element-1117">sk_live_...</code> - Production environment (real transactions)
        </li>
      </ul>

      <Callout type="tip" data-id="element-1118">
        Generate API keys in your Obseq dashboard under Settings → API Keys.
      </Callout>

      <h2 id="header" data-id="element-1119">Authentication Header</h2>
      <p data-id="element-1120">
        Include your API key in the <code data-id="element-1121">Authorization</code> header using the
        Bearer scheme:
      </p>
      <pre className="bg-slate-900 text-slate-100 p-4 rounded-lg my-4" data-id="element-1122">
        <code data-id="element-1123">Authorization: Bearer sk_live_abc123...</code>
      </pre>

      <h3 data-id="element-1124">Optional: Sub-Account Header</h3>
      <p data-id="element-1125">
        When acting on behalf of a platform's sub-account, include the{' '}
        <code data-id="element-1126">Obseq-Account</code> header:
      </p>
      <pre className="bg-slate-900 text-slate-100 p-4 rounded-lg my-4" data-id="element-1127">
        <code data-id="element-1128">{`Authorization: Bearer sk_live_abc123...
Obseq-Account: acct_sub_xyz789...`}</code>
      </pre>

      <h2 id="environments" data-id="element-1129">Environments</h2>
      <p data-id="element-1130">Obseq provides two environments with separate API keys:</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-6" data-id="element-1131">
        <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-6" data-id="element-1132">
          <div className="flex items-center gap-3 mb-3" data-id="element-1133">
            <Key className="w-6 h-6 text-blue-600" data-id="element-1134" />
            <h3 className="text-lg font-semibold text-blue-900" data-id="element-1135">Sandbox</h3>
          </div>
          <p className="text-sm text-blue-800 mb-3" data-id="element-1136">
            Test environment for development and integration testing.
          </p>
          <ul className="text-sm text-blue-700 space-y-1" data-id="element-1137">
            <li data-id="element-1138">• No real money movement</li>
            <li data-id="element-1139">• Simulated responses</li>
            <li data-id="element-1140">• Test card numbers accepted</li>
            <li data-id="element-1141">
              • Keys start with{' '}
              <code className="bg-blue-100 px-1 rounded" data-id="element-1142">sk_test_</code>
            </li>
          </ul>
        </div>

        <div className="bg-green-50 border-2 border-green-200 rounded-lg p-6" data-id="element-1143">
          <div className="flex items-center gap-3 mb-3" data-id="element-1144">
            <Shield className="w-6 h-6 text-green-600" data-id="element-1145" />
            <h3 className="text-lg font-semibold text-green-900" data-id="element-1146">Production</h3>
          </div>
          <p className="text-sm text-green-800 mb-3" data-id="element-1147">
            Live environment for real transactions.
          </p>
          <ul className="text-sm text-green-700 space-y-1" data-id="element-1148">
            <li data-id="element-1149">• Real money movement</li>
            <li data-id="element-1150">• Actual payment processing</li>
            <li data-id="element-1151">• Requires KYB verification</li>
            <li data-id="element-1152">
              • Keys start with{' '}
              <code className="bg-green-100 px-1 rounded" data-id="element-1153">sk_live_</code>
            </li>
          </ul>
        </div>
      </div>

      <Callout type="warning" data-id="element-1154">
        Never use production keys in development or testing environments. Always
        use sandbox keys for testing.
      </Callout>

      <h2 id="examples" data-id="element-1155">Code Examples</h2>
      <p data-id="element-1156">Here's how to make an authenticated request in different languages:</p>
      <CodeBlock code={authRequestCode} data-id="element-1157" />

      <h2 id="security" data-id="element-1158">Security Best Practices</h2>
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-6 my-6" data-id="element-1159">
        <div className="flex items-start gap-3 mb-4" data-id="element-1160">
          <AlertTriangle className="w-6 h-6 text-orange-600 flex-shrink-0 mt-1" data-id="element-1161" />
          <div data-id="element-1162">
            <h3 className="text-lg font-semibold text-slate-900 mb-2" data-id="element-1163">
              Protect Your API Keys
            </h3>
            <p className="text-sm text-slate-600" data-id="element-1164">
              API keys have full access to your account. Treat them like
              passwords.
            </p>
          </div>
        </div>

        <div className="space-y-4" data-id="element-1165">
          <div data-id="element-1166">
            <h4 className="font-semibold text-slate-900 mb-2" data-id="element-1167">✓ Do</h4>
            <ul className="text-sm text-slate-700 space-y-1" data-id="element-1168">
              <li data-id="element-1169">• Store keys in environment variables or secure vaults</li>
              <li data-id="element-1170">
                • Use separate keys for each environment (sandbox/production)
              </li>
              <li data-id="element-1171">• Rotate keys regularly (every 90 days recommended)</li>
              <li data-id="element-1172">• Use least-privilege access when possible</li>
              <li data-id="element-1173">• Revoke keys immediately if compromised</li>
              <li data-id="element-1174">• Monitor API key usage for suspicious activity</li>
            </ul>
          </div>

          <div data-id="element-1175">
            <h4 className="font-semibold text-slate-900 mb-2" data-id="element-1176">✗ Don't</h4>
            <ul className="text-sm text-slate-700 space-y-1" data-id="element-1177">
              <li data-id="element-1178">• Commit keys to version control (Git, SVN, etc.)</li>
              <li data-id="element-1179">• Share keys via email, Slack, or other messaging</li>
              <li data-id="element-1180">
                • Embed keys in client-side code (JavaScript, mobile apps)
              </li>
              <li data-id="element-1181">• Use production keys in development environments</li>
              <li data-id="element-1182">• Log API keys in application logs</li>
            </ul>
          </div>
        </div>
      </div>

      <h3 data-id="element-1183">Key Rotation</h3>
      <p data-id="element-1184">Rotate API keys regularly to minimize security risks:</p>
      <ol data-id="element-1185">
        <li data-id="element-1186">Generate a new API key in your dashboard</li>
        <li data-id="element-1187">Update your application to use the new key</li>
        <li data-id="element-1188">Test thoroughly in sandbox first</li>
        <li data-id="element-1189">Deploy the change to production</li>
        <li data-id="element-1190">Revoke the old key after confirming the new key works</li>
      </ol>

      <Callout type="note" data-id="element-1191">
        For complete authentication specifications, see{' '}
        <a href="#/docs/api-conventions" className="text-blue-600 hover:text-blue-700" data-id="element-1192">
          API Conventions
        </a>
        .
      </Callout>

      <h3 data-id="element-1193">Troubleshooting</h3>
      <table className="w-full text-sm my-6" data-id="element-1194">
        <thead data-id="element-1195">
          <tr className="border-b border-slate-200" data-id="element-1196">
            <th className="text-left py-2 pr-4" data-id="element-1197">Error</th>
            <th className="text-left py-2" data-id="element-1198">Solution</th>
          </tr>
        </thead>
        <tbody data-id="element-1199">
          <tr className="border-b border-slate-100" data-id="element-1200">
            <td className="py-3 pr-4" data-id="element-1201">
              <code data-id="element-1202">401 Unauthorized</code>
            </td>
            <td className="py-3 text-slate-600" data-id="element-1203">
              Check API key is correct and not expired
            </td>
          </tr>
          <tr className="border-b border-slate-100" data-id="element-1204">
            <td className="py-3 pr-4" data-id="element-1205">
              <code data-id="element-1206">Invalid API key</code>
            </td>
            <td className="py-3 text-slate-600" data-id="element-1207">
              Verify key format and environment (test vs live)
            </td>
          </tr>
          <tr className="border-b border-slate-100" data-id="element-1208">
            <td className="py-3 pr-4" data-id="element-1209">
              <code data-id="element-1210">API key revoked</code>
            </td>
            <td className="py-3 text-slate-600" data-id="element-1211">
              Generate a new key in your dashboard
            </td>
          </tr>
        </tbody>
      </table>

      <div className="mt-12 pt-8 border-t border-slate-200" data-id="element-1212">
        <h3 className="text-lg font-semibold text-slate-900 mb-4" data-id="element-1213">
          Related Guides
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1214">
          <a href="#/docs/guides/getting-started" className="p-4 bg-slate-50 border border-slate-200 rounded-lg hover:border-blue-300 transition-colors" data-id="element-1215">
            <h4 className="font-semibold text-slate-900 mb-1" data-id="element-1216">
              Getting Started
            </h4>
            <p className="text-sm text-slate-600" data-id="element-1217">Make your first API call</p>
          </a>
          <a href="#/docs/api-conventions" className="p-4 bg-slate-50 border border-slate-200 rounded-lg hover:border-blue-300 transition-colors" data-id="element-1218">
            <h4 className="font-semibold text-slate-900 mb-1" data-id="element-1219">
              API Conventions
            </h4>
            <p className="text-sm text-slate-600" data-id="element-1220">
              Complete API specifications
            </p>
          </a>
        </div>
      </div>
    </DocsLayout>;
}